import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Panel, StatusBadge } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { ShieldCheck } from "lucide-react";

export default function Connections() {
  const [d, setD] = useState(null);
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [exKey, setExKey] = useState("");
  const [exSecret, setExSecret] = useState("");
  const [exBusy, setExBusy] = useState(false);
  const [exState, setExState] = useState(null);
  const { reloadLayout } = useOutletContext() || {};

  const load = () => api.get("/connections").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/connections/crypto-com-app/credentials", { api_key: key, api_secret: secret });
      toast.success("Credentials encrypted and stored server-side");
      setKey(""); setSecret("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try { await api.delete("/connections/crypto-com-app/credentials"); toast.success("Credentials removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveExchange = async (e) => {
    e.preventDefault();
    setExBusy(true);
    try {
      await api.post("/connections/crypto-com-exchange/credentials", { api_key: exKey, api_secret: exSecret });
      toast.success("Exchange credentials encrypted and stored server-side");
      setExKey(""); setExSecret("");
      load(); reloadLayout?.();
    } catch (err) { toast.error(apiError(err)); } finally { setExBusy(false); }
  };

  const removeExchange = async () => {
    try { await api.delete("/connections/crypto-com-exchange/credentials"); toast.success("Exchange credentials removed"); setExState(null); load(); reloadLayout?.(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const testExchange = async () => {
    setExBusy(true);
    try {
      const r = await api.post("/connections/crypto-com-exchange/test");
      if (r.data.status === "CONNECTED") toast.success(`Exchange read-only connection verified · USD cash $${(r.data.usd_cash ?? 0).toLocaleString()}`);
      else toast.error(r.data.message || "Exchange verification failed");
      load(); reloadLayout?.();
    } catch (err) { toast.error(apiError(err)); } finally { setExBusy(false); }
  };

  const verifyExchange = async () => {
    setExBusy(true);
    try {
      const r = await api.get("/connections/crypto-com-exchange/state");
      setExState(r.data);
      if (r.data.matches === true) toast.success("External state reconciles with the internal ledger");
      else if (r.data.matches === false) toast.error(`External/internal mismatch — Δ$${r.data.delta}`);
      reloadLayout?.();
    } catch (err) { toast.error(apiError(err)); } finally { setExBusy(false); }
  };

  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="connections-page">
      <h2 className="font-display text-xl font-bold">Connections</h2>
      <div className="panel border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 flex items-center gap-3" data-testid="no-withdrawal-notice">
        <ShieldCheck className="w-5 h-5 gold-text shrink-0" />
        <p className="text-xs text-slate-300">{d.withdrawal_authority}. Credentials are Fernet-encrypted at rest, never logged, and never exposed to the browser after entry.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {d.providers.map((p) => (
          <Panel key={p.provider} title={p.label} right={<StatusBadge status={p.status} testId={`conn-status-${p.provider}`} />} testId={`conn-${p.provider}`}>
            <div className="space-y-2 text-sm">
              {p.api_key_mask && <div className="text-slate-400">Key: <span className="font-mono">{p.api_key_mask}</span></div>}
              {p.updated_at && <div className="text-slate-500 text-xs">Updated {new Date(p.updated_at).toLocaleString()}</div>}
              <p className="text-slate-500 text-xs">{p.message || (p.simulated ? "Simulated execution at real market prices. Clearly labelled; never claims production connectivity." : "")}</p>
              {p.provider === "crypto_com_app" && p.status !== "DISCONNECTED" && (
                <Button data-testid="remove-credentials-button" onClick={remove} variant="outline" size="sm" className="border-red-800 text-red-400">Remove credentials</Button>
              )}
              {p.provider === "crypto_com_exchange" && p.status !== "DISCONNECTED" && (
                <div className="space-y-2 pt-1">
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="test-exchange-button" onClick={testExchange} disabled={exBusy} variant="outline" size="sm" className="border-[#3F4656] text-slate-300">
                      {exBusy ? "TESTING…" : "TEST CONNECTION"}
                    </Button>
                    <Button data-testid="verify-exchange-button" onClick={verifyExchange} disabled={exBusy} variant="outline" size="sm" className="border-[#D4AF37]/50 text-[#D4AF37]">VERIFY LIVE STATE</Button>
                    <Button data-testid="remove-exchange-credentials-button" onClick={removeExchange} variant="outline" size="sm" className="border-red-800 text-red-400">Remove</Button>
                  </div>
                  {p.usd_cash != null && (
                    <div className="text-xs text-slate-400 font-mono" data-testid="exchange-usd-cash">
                      External USD cash: ${p.usd_cash.toLocaleString()} · spot positions: {p.positions_count ?? 0}
                    </div>
                  )}
                  {exState && exState.status === "CONNECTED" && (
                    <div className={`text-[11px] font-mono px-2 py-1.5 rounded-sm border ${exState.matches ? "border-emerald-800 text-emerald-400" : "border-red-800 text-red-400"}`} data-testid="exchange-verification">
                      External ${exState.external.usd_cash.toLocaleString()} vs internal ${exState.internal.total_cash.toLocaleString()} · positions {exState.external.positions.length} · open orders {exState.external.open_orders} · {exState.matches ? "RECONCILED" : `MISMATCH Δ$${exState.delta}`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Panel>
        ))}
      </div>
      <Panel title="Configure Crypto.com App Agent Key" testId="credentials-form-panel">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="metric-label">API Key</Label>
            <Input data-testid="api-key-input" value={key} onChange={(e) => setKey(e.target.value)} required
              className="bg-[#121316] border-[#2D323E] font-mono mt-1" autoComplete="off" />
          </div>
          <div>
            <Label className="metric-label">API Secret</Label>
            <Input data-testid="api-secret-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} required
              className="bg-[#121316] border-[#2D323E] font-mono mt-1" autoComplete="new-password" />
          </div>
          <Button data-testid="store-credentials-button" type="submit" disabled={busy} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
            {busy ? "ENCRYPTING…" : "STORE ENCRYPTED"}
          </Button>
        </form>
        <p className="text-[10px] text-slate-600 mt-3">Never request or paste withdrawal-enabled keys. The adapter has no withdrawal code path. Trading budget status and emergency shutdown are enforced server-side by the Risk Governor and kill switch.</p>
      </Panel>
      <Panel title="Configure Crypto.com Exchange API Key (institutional rail)" testId="exchange-credentials-form-panel">
        <form onSubmit={saveExchange} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="metric-label">Exchange API Key</Label>
            <Input data-testid="exchange-key-input" value={exKey} onChange={(e) => setExKey(e.target.value)} required
              className="bg-[#121316] border-[#2D323E] font-mono mt-1" autoComplete="off" />
          </div>
          <div>
            <Label className="metric-label">Exchange API Secret</Label>
            <Input data-testid="exchange-secret-input" type="password" value={exSecret} onChange={(e) => setExSecret(e.target.value)} required
              className="bg-[#121316] border-[#2D323E] font-mono mt-1" autoComplete="new-password" />
          </div>
          <Button data-testid="store-exchange-credentials-button" type="submit" disabled={exBusy} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
            {exBusy ? "ENCRYPTING…" : "STORE ENCRYPTED"}
          </Button>
        </form>
        <div className="mt-4 space-y-3" data-testid="exchange-key-guide">
          <div className="border border-red-800 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="exchange-no-withdrawals-warning">
            <p className="text-xs font-bold text-red-400 font-mono tracking-wide">DO NOT ENABLE WITHDRAWALS</p>
            <p className="text-[11px] text-red-300/80 mt-0.5">The API key you create must never carry withdrawal permission. Hussar Treasury has no withdrawal code path, never falls back to another provider, and will never ask for a withdrawal-enabled key.</p>
          </div>
          <ol className="text-[11px] text-slate-400 space-y-1.5 list-decimal list-inside font-mono" data-testid="exchange-key-guide-steps">
            <li>Log in to the Crypto.com <span className="text-slate-200">Exchange</span> web platform (exchange.crypto.com). Exchange API keys are separate from the mobile App — App keys do not work here.</li>
            <li>Open the profile menu (top right) → <span className="text-slate-200">Manage Account → API Management</span>.</li>
            <li>Choose <span className="text-slate-200">Create a new API key</span>, label it (e.g. "Hussar Treasury Read-Only") and confirm with your 2FA code.</li>
            <li>Copy the API Key and Secret immediately — the Secret is shown only once and cannot be retrieved later.</li>
            <li>Permissions for Stage 1: keep the default <span className="text-emerald-400">Can Read</span> only. Leave <span className="text-slate-200">Enable Trading</span> unchecked until Stage 2 is separately authorized, and <span className="text-red-400">never</span> check <span className="text-red-400">Enable Withdrawal</span>.</li>
            <li>Paste the key and secret below. They are Fernet-encrypted server-side, isolated to the selected Hussar Treasury account, and never returned to this browser after submission.</li>
          </ol>
          <p className="text-[10px] text-slate-600">Stage 1 is read-only verification (balances, positions, markets, open orders, health) — this account is NOT marked provider-verified until a live signed Exchange call succeeds. Stage 2 places orders ONLY in LIVE-CONFIRM mode through the approved-proposal pipeline. On connectivity failure the account stays PAPER-gated.</p>
        </div>
      </Panel>
    </div>
  );
}
